'use client';

import * as React from 'react';
import {
    Smartphone,
    Tablet,
    Laptop,
    Monitor,
    Globe,
    Megaphone,
    Chrome,
    AlertTriangle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

// ============================================================
// DEVICE TYPE ICONS
// Production values: mobile, tablet, smarttv, xr, console, NULL (desktop)
// ============================================================

export type DeviceType = 'mobile' | 'tablet' | 'desktop' | 'smarttv' | 'xr' | 'console' | 'unknown' | string;

interface DeviceTypeIconProps {
    deviceType: DeviceType | null;
    className?: string;
}

/**
 * Returns the appropriate icon for a device type
 * Note: NULL device_type typically means Desktop (Windows/macOS/Linux users)
 */
export function DeviceTypeIcon({ deviceType, className }: DeviceTypeIconProps) {
    const type = deviceType?.toLowerCase() || '';
    const iconClass = cn('h-4 w-4', className);

    if (type === 'mobile') {
        return <Smartphone className={iconClass} />;
    }
    if (type === 'tablet') {
        return <Tablet className={iconClass} />;
    }
    if (type === 'smarttv') {
        return <Monitor className={iconClass} />;
    }
    if (type === 'xr' || type === 'console') {
        return <Monitor className={iconClass} />;
    }
    // NULL or empty = Desktop (Windows/macOS/Linux users don't report device_type)
    return <Laptop className={iconClass} />;
}

/**
 * Get device type display name
 * Note: NULL device_type typically means Desktop
 */
export function getDeviceTypeLabel(deviceType: DeviceType | null): string {
    const type = deviceType?.toLowerCase() || '';
    if (type === 'mobile') return 'Mobile';
    if (type === 'tablet') return 'Tablet';
    if (type === 'smarttv') return 'Smart TV';
    if (type === 'xr') return 'VR/AR';
    if (type === 'console') return 'Console';
    // NULL or empty = Desktop
    if (!type || type === 'unknown') return 'Desktop';
    return deviceType || 'Desktop';
}

// ============================================================
// BROWSER ICONS (SVG)
// ============================================================

// Production browser values from devices table
export type BrowserName =
    // Desktop browsers
    | 'Chrome'
    | 'Safari'
    | 'Firefox'
    | 'Edge'
    | 'Opera'
    | 'Brave'
    | 'DuckDuckGo'
    | 'Yandex'
    | 'Qwant'
    | 'Ecosia'
    // Mobile browsers
    | 'Mobile Safari'
    | 'Mobile Chrome'
    | 'Mobile Firefox'
    | 'Samsung Internet'
    | 'Android Browser'
    | 'MIUI Browser'
    | 'Huawei Browser'
    | 'HeyTap'
    | 'Vivo Browser'
    // In-app browsers (payment limitations)
    | 'Facebook'
    | 'Instagram'
    | 'TikTok'
    | 'Snapchat'
    | 'Twitter'
    | 'WeChat'
    | 'NAVER'
    | 'Daum'
    | 'Line'
    | 'KAKAOTALK'
    | 'GSA' // Google Search App
    // WebViews
    | 'Chrome WebView'
    | 'WebKit'
    // Other
    | 'Klarna'
    | 'Silk'
    | 'Opera Touch'
    | 'Opera GX'
    | 'Chrome Headless'
    | 'unknown'
    | ''
    | string;

interface BrowserIconProps {
    browser: BrowserName;
    className?: string;
}

// In-app browsers that have payment limitations
// Based on production data: Facebook (506K), Instagram (200K), TikTok (77K), Snapchat (7K), etc.
const IN_APP_BROWSERS = [
    'facebook',
    'instagram',
    'tiktok',
    'snapchat',
    'twitter',
    'wechat',
    'naver',
    'daum',
    'line',
    'kakaotalk',
    'linkedin',
    'pinterest',
    'webview',
    'gsa', // Google Search App
];

/**
 * Check if a browser is an in-app browser
 * Production browsers with in-app issues: Facebook, Instagram, TikTok, Snapchat, WeChat, NAVER, Daum, Line, KAKAOTALK
 */
export function isInAppBrowser(browser: string): boolean {
    const lower = browser?.toLowerCase() || '';
    return IN_APP_BROWSERS.some(inApp => lower.includes(inApp));
}

/**
 * Chrome Browser Icon
 */
function ChromeIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#fff" />
            <path d="M12 2a10 10 0 0 0-8.66 5h7.66a5 5 0 0 1 4.33 2.5l3.53-6.12A10 10 0 0 0 12 2z" fill="#EA4335" />
            <path d="M21.17 8H12a5 5 0 0 1 0 8l-3.53 6.12a10 10 0 0 0 12.7-14.12z" fill="#FBBC05" />
            <path d="M3.34 7a10 10 0 0 0 5.13 13.12L12 14a5 5 0 0 1-4.33-7.5H3.34z" fill="#34A853" />
            <circle cx="12" cy="12" r="4" fill="#4285F4" />
        </svg>
    );
}

/**
 * Safari Browser Icon
 */
function SafariIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#fff" stroke="#e5e5e5" strokeWidth="0.5" />
            <circle cx="12" cy="12" r="9" fill="url(#safariGradient)" />
            <polygon points="12,4 13,12 12,20 11,12" fill="#EA4335" />
            <polygon points="4,12 12,11 20,12 12,13" fill="#fff" />
            <circle cx="12" cy="12" r="2" fill="#fff" />
            <defs>
                <linearGradient id="safariGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#19D7FF" />
                    <stop offset="100%" stopColor="#1E64F0" />
                </linearGradient>
            </defs>
        </svg>
    );
}

/**
 * Firefox Browser Icon
 */
function FirefoxIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#FF7139" />
            <path d="M12 4c-4.4 0-8 3.6-8 8s3.6 8 8 8c2.8 0 5.3-1.5 6.7-3.7.4-.7.1-1.5-.6-1.9-.7-.4-1.5-.1-1.9.6-.9 1.5-2.5 2.4-4.2 2.4-2.8 0-5-2.2-5-5s2.2-5 5-5c1.7 0 3.3.9 4.2 2.4.4.7 1.2 1 1.9.6.7-.4 1-.9.6-1.6C17.3 5.5 14.8 4 12 4z" fill="#fff" opacity="0.3" />
        </svg>
    );
}

/**
 * Edge Browser Icon
 */
function EdgeIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#0078D4" />
            <path d="M6 12c0-3.3 2.7-6 6-6s6 2.7 6 6-2.7 6-6 6" stroke="#fff" strokeWidth="2" fill="none" />
            <circle cx="12" cy="12" r="2" fill="#50E6FF" />
        </svg>
    );
}

/**
 * Instagram Icon
 */
function InstagramIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
            <defs>
                <linearGradient id="igGradient" x1="0%" y1="100%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#FFDC80" />
                    <stop offset="25%" stopColor="#F77737" />
                    <stop offset="50%" stopColor="#F56040" />
                    <stop offset="75%" stopColor="#C13584" />
                    <stop offset="100%" stopColor="#833AB4" />
                </linearGradient>
            </defs>
            <rect x="2" y="2" width="20" height="20" rx="5" fill="url(#igGradient)" />
            <circle cx="12" cy="12" r="4" stroke="#fff" strokeWidth="2" fill="none" />
            <circle cx="17.5" cy="6.5" r="1.5" fill="#fff" />
        </svg>
    );
}

/**
 * Facebook Icon
 */
function FacebookIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#1877F2" />
            <path d="M15.5 8H14c-.83 0-1 .67-1 1.5V11h2.5l-.5 2.5H13V20h-3v-6.5H8V11h2V9c0-2 1.5-3 3.5-3h2v2z" fill="#fff" />
        </svg>
    );
}

/**
 * TikTok Icon
 */
function TikTokIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
            <rect x="2" y="2" width="20" height="20" rx="4" fill="#000" />
            <path d="M16.5 8.5c-1-.5-1.5-1.5-1.5-2.5h-2v9c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2v-2c-2.2 0-4 1.8-4 4s1.8 4 4 4 4-1.8 4-4V9.5c.8.5 1.8.5 2 .5V8c-.3 0-.5 0-.5.5z" fill="#25F4EE" />
            <path d="M17 8.5c-.8-.5-1.5-1.5-1.5-2.5h-1v9c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2v-1c-1.7 0-3 1.3-3 3s1.3 3 3 3 3-1.3 3-3V10c.7.3 1.5.5 2 .5V9c-.2 0-.3-.3-.5-.5z" fill="#FE2C55" />
        </svg>
    );
}

/**
 * Returns the appropriate icon for a browser
 * Based on production data (top browsers):
 * Facebook (506K), Mobile Safari (270K), Chrome (252K), Mobile Chrome (222K),
 * Instagram (200K), TikTok (77K), Samsung Internet (30K), Safari (28K),
 * Edge (20K), GSA (19K), Chrome WebView (14K), unknown (14K), WebKit (9K),
 * Firefox (7.5K), Snapchat (7K), Android Browser (6K), WeChat, NAVER, Daum, etc.
 */
export function BrowserIcon({ browser, className }: BrowserIconProps) {
    const name = browser?.toLowerCase() || '';
    const iconClass = cn('h-4 w-4', className);

    // In-app browsers (payment limitations) - check first as they're most common
    if (name === 'facebook' || name.includes('facebook')) {
        return <FacebookIcon className={iconClass} />;
    }
    if (name === 'instagram' || name.includes('instagram')) {
        return <InstagramIcon className={iconClass} />;
    }
    if (name === 'tiktok' || name.includes('tiktok')) {
        return <TikTokIcon className={iconClass} />;
    }
    if (name === 'snapchat' || name.includes('snapchat')) {
        return <Globe className={cn(iconClass, 'text-yellow-500')} />;
    }

    // Safari variants (2nd most common mobile browser)
    if (name.includes('safari')) {
        return <SafariIcon className={iconClass} />;
    }

    // Chrome variants (3rd most common)
    if (name.includes('chrome') && !name.includes('webview')) {
        return <ChromeIcon className={iconClass} />;
    }

    // Samsung Internet
    if (name.includes('samsung')) {
        return <Globe className={cn(iconClass, 'text-purple-500')} />;
    }

    // Edge
    if (name.includes('edge')) {
        return <EdgeIcon className={iconClass} />;
    }

    // GSA (Google Search App) - in-app browser
    if (name === 'gsa') {
        return <ChromeIcon className={iconClass} />;
    }

    // WebView / Chrome WebView
    if (name.includes('webview')) {
        return <Chrome className={cn(iconClass, 'text-amber-500')} />;
    }

    // Firefox
    if (name.includes('firefox')) {
        return <FirefoxIcon className={iconClass} />;
    }

    // Android Browser
    if (name.includes('android browser')) {
        return <Globe className={cn(iconClass, 'text-green-500')} />;
    }

    // Asian in-app browsers
    if (name.includes('wechat')) {
        return <Globe className={cn(iconClass, 'text-green-600')} />;
    }
    if (name.includes('naver') || name.includes('daum')) {
        return <Globe className={cn(iconClass, 'text-green-500')} />;
    }
    if (name.includes('kakaotalk')) {
        return <Globe className={cn(iconClass, 'text-yellow-400')} />;
    }
    if (name.includes('line')) {
        return <Globe className={cn(iconClass, 'text-green-400')} />;
    }

    // Other browsers
    if (name.includes('opera')) {
        return <Globe className={cn(iconClass, 'text-red-500')} />;
    }
    if (name.includes('brave')) {
        return <Globe className={cn(iconClass, 'text-orange-500')} />;
    }
    if (name.includes('duckduckgo')) {
        return <Globe className={cn(iconClass, 'text-orange-400')} />;
    }
    if (name.includes('webkit')) {
        return <SafariIcon className={iconClass} />;
    }

    // Default globe icon for unknown/''/etc.
    return <Globe className={iconClass} />;
}

/**
 * Get browser display name
 * Handles all production browser values
 */
export function getBrowserLabel(browser: string): string {
    const name = browser?.toLowerCase() || '';

    // Empty or unknown
    if (!name || name === 'unknown') return 'Unknown';

    // Exact matches first (for common browsers)
    if (name === 'mobile safari') return 'Mobile Safari';
    if (name === 'mobile chrome') return 'Mobile Chrome';
    if (name === 'mobile firefox') return 'Mobile Firefox';
    if (name === 'chrome webview') return 'Chrome WebView';
    if (name === 'chrome headless') return 'Chrome Headless';
    if (name === 'samsung internet') return 'Samsung Internet';
    if (name === 'android browser') return 'Android Browser';
    if (name === 'opera touch') return 'Opera Touch';
    if (name === 'opera gx') return 'Opera GX';
    if (name === 'miui browser') return 'MIUI Browser';
    if (name === 'huawei browser') return 'Huawei Browser';
    if (name === 'vivo browser') return 'Vivo Browser';
    if (name === 'avast secure browser') return 'Avast Secure';
    if (name === 'avg secure browser') return 'AVG Secure';
    if (name === 'edge webview2') return 'Edge WebView';
    if (name === 'oculus browser') return 'Oculus VR';
    if (name === 'gsa') return 'Google Search';
    if (name === 'qqbrowser') return 'QQ Browser';

    // Partial matches
    if (name.includes('chrome')) return 'Chrome';
    if (name.includes('safari')) return 'Safari';
    if (name.includes('firefox')) return 'Firefox';
    if (name.includes('edge')) return 'Edge';
    if (name.includes('opera')) return 'Opera';
    if (name.includes('brave')) return 'Brave';
    if (name.includes('duckduckgo')) return 'DuckDuckGo';
    if (name.includes('ecosia')) return 'Ecosia';
    if (name.includes('yandex')) return 'Yandex';
    if (name.includes('webkit')) return 'WebKit';
    if (name.includes('silk')) return 'Silk (Amazon)';
    if (name.includes('klarna')) return 'Klarna';

    // In-app browsers
    if (name.includes('facebook')) return 'Facebook';
    if (name.includes('instagram')) return 'Instagram';
    if (name.includes('tiktok')) return 'TikTok';
    if (name.includes('snapchat')) return 'Snapchat';
    if (name.includes('wechat')) return 'WeChat';
    if (name.includes('twitter')) return 'Twitter/X';
    if (name.includes('line')) return 'LINE';
    if (name.includes('kakaotalk')) return 'KakaoTalk';
    if (name.includes('naver')) return 'NAVER';
    if (name.includes('daum')) return 'Daum';

    // Return original if no match (capitalized)
    return browser.charAt(0).toUpperCase() + browser.slice(1);
}

// ============================================================
// OPERATING SYSTEM ICONS
// ============================================================

// Production OS values from devices table
export type OSName =
    | 'iOS'
    | 'Android'
    | 'Windows'
    | 'macOS'
    | 'Linux'
    | 'Chrome OS'
    | 'Ubuntu'
    | 'Tizen'
    | 'HarmonyOS'
    | 'Windows Mobile'
    | 'PlayStation'
    | 'unknown'
    | ''
    | string;

interface OSIconProps {
    os: OSName;
    className?: string;
}

/**
 * Apple Icon (iOS / macOS)
 */
function AppleIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
        </svg>
    );
}

/**
 * Android Icon
 */
function AndroidIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
            <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C6.97 3.26 6 5.01 6 7h12c0-1.99-.97-3.75-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z" fill="#3DDC84" />
        </svg>
    );
}

/**
 * Windows Icon
 */
function WindowsIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
            <path d="M3 12V6.5l7-1v6.5H3zm8-1V4.5l10-1.5v9H11zm0 2h10v9l-10-1.5V13zm-8 0h7v6l-7-1v-5z" fill="#00ADEF" />
        </svg>
    );
}

/**
 * Linux Icon
 */
function LinuxIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
            <path d="M12.504 0c-.155 0-.311.002-.465.007C8.267.079 5.8 2.314 5.123 5.59c-.284 1.366-.182 2.767.185 4.138.08.3.17.6.274.897-.46.55-.815 1.164-1.07 1.83-.28.73-.4 1.493-.365 2.248.035.75.23 1.47.565 2.136.26.52.58.993.96 1.42-.12.43-.16.87-.13 1.3.03.45.15.88.35 1.28.13.27.29.52.47.75-.23.73-.33 1.48-.28 2.22.04.58.14 1.15.32 1.7.16.52.4 1.01.7 1.47.42.64 1.01 1.16 1.71 1.53.48.25 1 .42 1.53.51.6.1 1.22.12 1.83.04.69-.09 1.35-.32 1.95-.67.48-.28.91-.63 1.29-1.03.37.08.74.14 1.11.18.53.06 1.06.05 1.58-.02.7-.1 1.37-.33 1.99-.68.61-.35 1.15-.82 1.6-1.38.44-.55.76-1.18.94-1.85.16-.58.22-1.18.18-1.78-.03-.54-.15-1.07-.35-1.58.16-.19.31-.4.44-.62.37-.62.6-1.3.69-2 .09-.73.03-1.47-.18-2.18-.14-.46-.35-.9-.61-1.3.3-.78.44-1.6.43-2.42-.02-.89-.21-1.76-.56-2.58-.33-.76-.8-1.44-1.38-2.02.15-.92.07-1.85-.23-2.73-.32-.92-.86-1.74-1.58-2.4-.5-.46-1.08-.83-1.7-1.1-.5-.2-1-.35-1.52-.43-.37-.06-.75-.09-1.13-.09zm-.87 1.67c.16-.01.32 0 .48.01.36.04.71.11 1.05.24.26.1.5.23.73.38.31.2.59.44.83.7.22.24.41.5.57.78l.04.07c.06.11.12.23.17.35.14.36.22.73.22 1.1 0 .26-.04.53-.1.79-.04.18-.09.36-.15.53-.45-.2-.93-.34-1.42-.42-.35-.06-.71-.08-1.07-.08-.77 0-1.54.1-2.28.3-.35.09-.7.21-1.03.35-.09-.26-.15-.52-.18-.79-.03-.24-.03-.49-.01-.73.06-.51.22-1 .46-1.46.15-.29.34-.56.56-.8.25-.27.54-.5.85-.7.4-.25.83-.43 1.28-.53zm1.71 4.08c.29 0 .57.02.86.06.37.06.74.16 1.09.3.22.09.42.2.61.32.22.14.42.3.6.47.15.14.28.29.39.45.1.14.18.29.24.45.06.16.09.32.09.49 0 .2-.04.4-.12.58-.1.23-.24.44-.42.62-.21.21-.46.38-.73.51-.3.14-.63.24-.96.29-.43.06-.87.07-1.31.02-.43-.05-.86-.15-1.27-.3-.32-.11-.62-.27-.9-.46-.24-.16-.46-.35-.66-.56-.18-.2-.33-.42-.44-.66-.1-.22-.16-.46-.17-.7 0-.24.05-.48.15-.7.11-.25.27-.48.47-.68.22-.22.48-.4.76-.54.33-.17.69-.29 1.06-.36.32-.06.65-.09.97-.09v-.01z" />
        </svg>
    );
}

/**
 * Returns the appropriate icon for an operating system
 * Based on production data:
 * iOS (821K), Android (598K), Windows (203K), macOS (64K), Linux (20K),
 * '' (17K), Chrome OS (2K), unknown (352), Ubuntu, Tizen, HarmonyOS, etc.
 */
export function OSIcon({ os, className }: OSIconProps) {
    const name = os?.toLowerCase() || '';
    const iconClass = cn('h-4 w-4', className);

    // Empty or unknown - default to desktop
    if (!name || name === 'unknown') {
        return <Monitor className={iconClass} />;
    }

    // iOS / iPadOS
    if (name === 'ios' || name.includes('ipad')) {
        return <AppleIcon className={iconClass} />;
    }

    // macOS
    if (name === 'macos' || name.includes('mac os') || name.includes('osx')) {
        return <AppleIcon className={iconClass} />;
    }

    // Android (handle lowercase 'android' variant too)
    if (name === 'android' || name.includes('android') || name === 'harmonyos') {
        return <AndroidIcon className={iconClass} />;
    }

    // Windows (including Windows Mobile)
    if (name.includes('windows')) {
        return <WindowsIcon className={iconClass} />;
    }

    // Chrome OS
    if (name === 'chrome os' || name.includes('chromeos')) {
        return <ChromeIcon className={iconClass} />;
    }

    // Linux variants
    if (name === 'linux' || name === 'ubuntu' || name === 'debian' || 
        name === 'fedora' || name === 'mint' || name === 'slackware' ||
        name.includes('linux')) {
        return <LinuxIcon className={iconClass} />;
    }

    // Tizen (Samsung TVs, watches)
    if (name === 'tizen') {
        return <Monitor className={iconClass} />;
    }

    // PlayStation
    if (name === 'playstation') {
        return <Monitor className={iconClass} />;
    }

    // Default
    return <Monitor className={iconClass} />;
}

/**
 * Get OS display name
 * Handles all production OS values
 */
export function getOSLabel(os: string): string {
    const name = os?.toLowerCase() || '';

    // Empty or unknown
    if (!name || name === 'unknown') return 'Unknown';

    // Exact matches
    if (name === 'ios') return 'iOS';
    if (name === 'android') return 'Android';
    if (name === 'windows') return 'Windows';
    if (name === 'macos') return 'macOS';
    if (name === 'linux') return 'Linux';
    if (name === 'chrome os') return 'Chrome OS';
    if (name === 'ubuntu') return 'Ubuntu';
    if (name === 'tizen') return 'Tizen';
    if (name === 'harmonyos') return 'HarmonyOS';
    if (name === 'windows mobile') return 'Windows Mobile';
    if (name === 'playstation') return 'PlayStation';
    if (name === 'chromecast linux') return 'Chromecast';

    // Partial matches
    if (name.includes('ipad')) return 'iPadOS';
    if (name.includes('macos') || name.includes('mac os')) return 'macOS';
    if (name.includes('android')) return 'Android';
    if (name.includes('windows')) return 'Windows';
    if (name.includes('linux')) return 'Linux';

    // Return original if no match (capitalized)
    return os.charAt(0).toUpperCase() + os.slice(1);
}

// ============================================================
// UTM / TRAFFIC SOURCE ICONS
// ============================================================

interface UTMSourceIconProps {
    source: string;
    medium?: string;
    className?: string;
}

/**
 * Returns the appropriate icon for a UTM source
 */
export function UTMSourceIcon({ source, medium, className }: UTMSourceIconProps) {
    const src = source?.toLowerCase() || '';
    const med = medium?.toLowerCase() || '';
    const iconClass = cn('h-4 w-4', className);

    // Facebook
    if (src.includes('facebook') || src === 'fb') {
        return <FacebookIcon className={iconClass} />;
    }

    // Instagram
    if (src.includes('instagram') || src === 'ig') {
        return <InstagramIcon className={iconClass} />;
    }

    // TikTok
    if (src.includes('tiktok')) {
        return <TikTokIcon className={iconClass} />;
    }

    // Google (Ads, Organic)
    if (src.includes('google')) {
        return <ChromeIcon className={iconClass} />;
    }

    // Paid traffic
    if (med === 'paid' || med === 'cpc' || med === 'ppc') {
        return <Megaphone className={cn(iconClass, 'text-amber-500')} />;
    }

    // Direct
    if (src === '(direct)' || src === 'direct') {
        return <Globe className={cn(iconClass, 'text-blue-500')} />;
    }

    // Default
    return <Globe className={iconClass} />;
}

/**
 * Get UTM source display name
 */
export function getUTMSourceLabel(source: string, medium?: string): string {
    const src = source?.toLowerCase() || '(direct)';
    const med = medium?.toLowerCase() || '(none)';

    if (src === '(direct)') return 'Direct';
    if (src.includes('facebook') || src === 'fb') return 'Facebook';
    if (src.includes('instagram') || src === 'ig') return 'Instagram';
    if (src.includes('tiktok')) return 'TikTok';
    if (src.includes('google')) return 'Google';

    return source;
}

// ============================================================
// WARNING BADGE FOR IN-APP BROWSERS
// ============================================================

interface InAppBrowserWarningProps {
    className?: string;
}

/**
 * Warning indicator for in-app browsers
 */
export function InAppBrowserWarning({ className }: InAppBrowserWarningProps) {
    const { t } = useTranslation('common');
    return (
        <span
            className={cn(
                'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium',
                'bg-amber-100 text-amber-700 border border-amber-200',
                'dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
                className
            )}
            title={t('deviceIcons.inAppBrowserWarning')}
        >
            <AlertTriangle className="h-2.5 w-2.5" />
            in-app
        </span>
    );
}

